// src/pages/FundPlanner/components/Funds/Main.jsx
import React, { useMemo, useCallback, useEffect, useState } from "react";
import {
  DropdownSelect,
  MultiSelectDropdown,
  NumberInput,
  TextInput,
  TextAreaInput,
} from "components/InputStyled";
import { useFunds } from "pages/FundPlanner/context/FundsContext";
import { useDialog } from "context/DialogContext";
import { SHARING_OPTIONS, normalizeSharing } from "lib/permissions";
import {
  DestinationPositionSelector,
  DestinationPositionBrowser,
  destinationPositionShortLabel,
} from "./DestinationPositionBrowser";

function portfolioGroupOptions(portfolios) {
  return Array.isArray(portfolios)
    ? portfolios.map((p) => ({
        value: p.id,
        label: p.name || "(Untitled Portfolio Group)",
      }))
    : [];
}

export default function Main() {
  const {
    currentName,
    currentId,
    description,
    fundType,
    portfolioIds,
    portfolios,
    yieldMax,
    sweepPositionId,
    sharing,
    setCurrentName,
    setDescription,
    setFundType,
    setPortfolioIds,
    setYieldMax,
    setSweepPositionId,
    setSharing,
    lastError,
    createPortfolioGroup,
    loadTransferDestinationPositions,
    isReadOnly,
    canChangeSharing,
  } = useFunds();

  const dialog = useDialog();
  const [showSweepBrowser, setShowSweepBrowser] = useState(false);
  const [expandedFundIds, setExpandedFundIds] = useState(new Set());
  const [sweepDestinations, setSweepDestinations] = useState([]);
  const [isLoadingSweepDestinations, setIsLoadingSweepDestinations] =
    useState(false);

  useEffect(() => {
    let active = true;

    if (!currentId) {
      setSweepDestinations([]);
      return () => {
        active = false;
      };
    }

    setIsLoadingSweepDestinations(true);
    loadTransferDestinationPositions().then((result) => {
      if (!active) return;
      setSweepDestinations(result?.ok ? result.data || [] : []);
      setIsLoadingSweepDestinations(false);
    });

    return () => {
      active = false;
    };
  }, [currentId, loadTransferDestinationPositions]);

  const selectedSweep = useMemo(
    () => sweepDestinations.find((row) => row.id === sweepPositionId),
    [sweepDestinations, sweepPositionId]
  );

  const portfolioDropdownOptions = useMemo(
    () => portfolioGroupOptions(portfolios),
    [portfolios]
  );

  const handleCreatePortfolioGroup = useCallback(
    async () => {
      const name = await dialog.prompt("Enter a Portfolio Group name.", {
        title: "Create Portfolio Group",
        label: "Name",
        okLabel: "Create",
        cancelLabel: "Cancel",
      });

      if (name == null) {
        return;
      }

      const trimmedName = String(name).trim();
      if (!trimmedName) {
        return;
      }

      const created = await createPortfolioGroup(trimmedName);

      if (created?.ok && created?.data?.id) {
        setPortfolioIds([...(portfolioIds || []), created.data.id]);
      }
    },
    [createPortfolioGroup, dialog, portfolioIds, setPortfolioIds]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <TextInput
          label="Fund Name"
          value={currentName || ""}
          onChange={setCurrentName}
          disabled={isReadOnly}
          className="min-w-72"
          placeholder="e.g., Operating Fund"
        />

        <DropdownSelect
          label="Fund Type"
          value={fundType || ""}
          onChange={setFundType}
          options={[
            { value: "bond_proceeds", label: "Bond Proceeds" },
            {
              value: "capital_reserve_stabilization",
              label: "Capital Reserve / Stabilization",
            },
            { value: "debt_service", label: "Debt Service" },
            {
              value: "insurance_risk_reserve",
              label: "Insurance / Risk Reserve",
            },
            { value: "operating", label: "Operating" },
            { value: "program", label: "Program" },
            { value: "restricted_revenue", label: "Restricted Revenue" },
            { value: "other", label: "Other" },
          ]}
          disabled={isReadOnly}
          className="min-w-56"
        />

        <MultiSelectDropdown
          label="Portfolio Groups"
          values={portfolioIds}
          onChange={setPortfolioIds}
          options={portfolioDropdownOptions}
          action={{ label: "+ Create New Portfolio Group", onClick: handleCreatePortfolioGroup }}
          disabled={isReadOnly}
          className="min-w-72"
        />

        {fundType === "bond_proceeds" ? (
          <NumberInput
            label="Yield Max / Arbitrage Yield"
            value={yieldMax}
            onChange={setYieldMax}
            type="percent"
            precision={9}
            disabled={isReadOnly}
            className="min-w-56"
          />
        ) : null}

        <div className="flex basis-full flex-col gap-1.5">
          <div className="flex flex-wrap items-end gap-6">
            <div className="flex min-w-0 flex-col">
              <span className="mb-1 text-xs font-medium text-muted">
                Sweep Account
              </span>
              <div className="flex min-w-80 gap-2">
                <DestinationPositionSelector
                  valueLabel={
                    selectedSweep
                      ? destinationPositionShortLabel(selectedSweep)
                      : sweepPositionId
                        ? "Configured position is unavailable"
                        : ""
                  }
                  placeholder="Select destination"
                  loading={isLoadingSweepDestinations}
                  disabled={isReadOnly || !currentId}
                  onBrowse={() => {
                    setExpandedFundIds(
                      new Set(
                        selectedSweep?.fund_id ? [selectedSweep.fund_id] : []
                      )
                    );
                    setShowSweepBrowser(true);
                  }}
                />
                {sweepPositionId && !isReadOnly ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setSweepPositionId("")}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col">
              <span className="mb-1 text-xs font-medium text-muted">
                Sharing
              </span>
              <DropdownSelect
                ariaLabel="Sharing"
                value={normalizeSharing(sharing)}
                onChange={setSharing}
                options={SHARING_OPTIONS}
                disabled={!canChangeSharing}
                className="min-w-56"
              />
            </div>
          </div>
          <span className="text-xs text-muted">
            Default is CASH if no sweep account designated.
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <TextAreaInput
          label="Description / Notes"
          value={description || ""}
          onChange={setDescription}
          disabled={isReadOnly}
          className="min-w-[32rem] flex-1"
          rows={4}
          placeholder="Optional description"
        />
      </div>

      {lastError ? <div className="text-sm text-red-600">{lastError}</div> : null}

      <DestinationPositionBrowser
        isOpen={showSweepBrowser}
        targets={sweepDestinations}
        selectedTargetId={sweepPositionId}
        searchText=""
        expandedGroupIds={expandedFundIds}
        onToggleGroup={(fundId) =>
          setExpandedFundIds((previous) => {
            const next = new Set(previous);
            if (next.has(fundId)) next.delete(fundId);
            else next.add(fundId);
            return next;
          })
        }
        onSelectTarget={(destination) => {
          setSweepPositionId(destination.id);
          setShowSweepBrowser(false);
        }}
        onClose={() => setShowSweepBrowser(false)}
        title="Select Sweep Account"
        description=""
        showSearch={false}
        compact
      />
    </div>
  );
}
